import React from 'react';
import { useSocket } from '../../contexts/SocketContext';
import { PlayerRole, formatString } from '@deadpan/shared';
import { useTranslations } from '../../hooks/useTranslations';

export const UndercoverGhostFactInput: React.FC = () => {
  const { socket, gameState, currentRoom, myRole, myPlayerId } = useSocket();
  const t = useTranslations();
  const u = t.games.undercover;
  const [fact, setFact] = React.useState('');
  const [isEditingSubmitted, setIsEditingSubmitted] = React.useState(false);
  const [now, setNow] = React.useState(() => Date.now());

  React.useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(interval);
  }, []);

  const myId = myPlayerId;
  const mySubmittedFact = (myId && gameState?.ghostFacts?.[myId]) || '';
  const hasSubmitted = mySubmittedFact.length > 0;
  const timer = gameState?.timer;
  const timeRemaining =
    timer && timer.timerEndTime > 0
      ? Math.max(0, Math.ceil((timer.timerEndTime - now) / 1000))
      : null;

  if (!gameState || !currentRoom) {
    return null;
  }

  const isLockedAfterSubmit = hasSubmitted && !isEditingSubmitted;
  const displayedFact = isLockedAfterSubmit ? mySubmittedFact : fact;

  const handleSubmit = () => {
    const trimmed = fact.trim();
    if (!socket || !trimmed || trimmed.length > 100) return;
    socket.emit('client:submit-ghost-fact', trimmed);
    setIsEditingSubmitted(false);
  };

  const participants = currentRoom.players.filter((player) => player.role !== PlayerRole.WORDSMITH);
  const submittedCount = participants.filter((player) => Boolean(gameState.ghostFacts?.[player.id])).length;

  const roleLabel =
    myRole === PlayerRole.UNDERCOVER
      ? t.roles.undercover
      : myRole === PlayerRole.CIVILIAN
        ? t.roles.civilian
        : u.ghostRoleLoading;

  const factInstruction =
    myRole === PlayerRole.UNDERCOVER
      ? u.ghostFactInstructionUndercover
      : myRole === PlayerRole.CIVILIAN
        ? u.ghostFactInstructionCivilian
        : u.ghostFactInstructionDefault;

  const identitySectionClass =
    myRole === PlayerRole.CIVILIAN
      ? 'border-2 border-amber-200 bg-gradient-to-br from-yellow-50 to-orange-50'
      : myRole === PlayerRole.UNDERCOVER
        ? 'border-2 border-rose-200 bg-gradient-to-br from-red-50 to-rose-50'
        : 'border-2 border-slate-200 bg-slate-50';
  const identityTitleClass =
    myRole === PlayerRole.CIVILIAN
      ? 'text-amber-900'
      : myRole === PlayerRole.UNDERCOVER
        ? 'text-rose-900'
        : 'text-slate-700';
  const identityLabelClass =
    myRole === PlayerRole.CIVILIAN
      ? 'text-amber-950'
      : myRole === PlayerRole.UNDERCOVER
        ? 'text-rose-950'
        : 'text-slate-900';

  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 w-[90%] max-w-[600px] border border-white/20">
      <div className={`w-full mb-4 p-3 sm:p-4 rounded-xl ${identitySectionClass}`}>
        <p className={`text-center text-sm font-bold ${identityTitleClass}`}>{u.ghostYourRole}</p>
        <p className={`text-center text-lg sm:text-xl font-extrabold mt-1 ${identityLabelClass}`}>{roleLabel}</p>
      </div>
      <div className="text-center mb-5">
        <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
          {u.ghostSubmitFactTitle}
        </h2>
        <p className="text-sm sm:text-base text-gray-700 mt-2">{factInstruction}</p>
        <p className="text-lg sm:text-xl font-bold text-red-600 mt-2">
          {formatString(u.ghostTimeRemaining, {
            time: timeRemaining === null ? '…' : `${timeRemaining}s`,
          })}
        </p>
        <p className="text-xs sm:text-sm text-gray-500 mt-1">
          {formatString(u.ghostSubmittedProgress, {
            submitted: String(submittedCount),
            total: String(participants.length),
          })}
        </p>
      </div>

      <div className="space-y-3">
        <textarea
          value={displayedFact}
          disabled={isLockedAfterSubmit}
          onChange={(e) => setFact(e.target.value.slice(0, 100))}
          rows={3}
          maxLength={100}
          placeholder={u.ghostFactPlaceholder}
          className="w-full p-3 border-2 border-orange-300 rounded-xl focus:outline-none focus:border-orange-500 disabled:bg-gray-100"
        />
        <div className="flex items-center justify-between text-xs sm:text-sm text-gray-600">
          <span>{displayedFact.length}/100</span>
          {hasSubmitted && !isEditingSubmitted && (
            <span className="text-green-600 font-semibold">{u.ghostSubmitted}</span>
          )}
        </div>
        {hasSubmitted && !isEditingSubmitted && (
          <button
            onClick={() => {
              setFact(mySubmittedFact);
              setIsEditingSubmitted(true);
            }}
            className="w-full bg-white border-2 border-orange-300 text-orange-700 py-3 rounded-xl font-bold"
          >
            {u.ghostEditFactButton}
          </button>
        )}
        {(!hasSubmitted || isEditingSubmitted) && (
          <button
            onClick={handleSubmit}
            disabled={fact.trim().length === 0 || fact.trim().length > 100}
            className="w-full bg-gradient-to-r from-orange-600 to-red-600 text-white py-3 rounded-xl font-bold disabled:from-gray-400 disabled:to-gray-400"
          >
            {hasSubmitted ? u.ghostResubmitFactButton : u.ghostSubmitFactButton}
          </button>
        )}
      </div>
    </div>
  );
};
